import React, { createRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@mui/material';
import { Item, ItemPhoto } from '@/types/item';
import { Modal } from '@/types/modal';
import { useItemPhotos } from '@/hooks/item';
import Container from '@/components/molecules/section/Container';
import ModalView from '@/components/modals/Modal';
import Photos, { OrderStatus } from '@/components/molecules/section/Photos';
import { HiddenFile } from '@/components/molecules/common/Form';
import { GridAction } from '@/components/molecules/section/Grid';
import { ServiceErrorView } from '@/components/atoms/Error';
import styles from './Photos.module.scss';

const View: React.FC<{ item: Item }> = ({ item }) => {
  const { t } = useTranslation();
  const [modal, setModal] = useState<Modal | null>(null);
  const imageRef = createRef<HTMLInputElement>();
  const [image, setImage] = useState<File | null>(null);
  const [open, setOpen] = useState(false);
  const [orderStatus, setOrderStatus] = useState<OrderStatus>('Init');
  const [photos, setPhotos] = useState<ItemPhoto[]>([]);

  const productId = item.productId;

  const data = useItemPhotos(productId, open);

  useEffect(() => {
    if (image) {
      setModal({ kind: 'ItemPhotoUpload', image, itemId: productId, onClose: () => setModal(null) });
    }
  }, [image]);

  useEffect(() => {
    if (productId) {
      // Reset the re-order photos state when user change to other products
      setOrderStatus('Init');
    }
  }, [productId]);

  useEffect(() => {
    // For issue when re-order photos, this hook is triggered make the move() not working
    if (orderStatus === 'Editing' || orderStatus === 'Completed') return;

    if (data.kind === 'Loaded') {
      setPhotos(data.data.data.sort((a, b) => Number(a.order) - Number(b.order)));
    }
  }, [data, orderStatus]);

  const isFetching = data.kind === 'Called' || (data.kind === 'Loaded' && data.refetching);

  useEffect(() => {
    if (orderStatus === 'Completed') {
      setModal({
        kind: 'ItemPhotoOrder',
        photos: photos.map(({ photoName }) => photoName),
        itemId: item.productId,
        onClose: () => {
          setOrderStatus('Init');
          setModal(null);
        },
      });
    }
  }, [orderStatus]);

  const actions: GridAction[] = [
    {
      kind: 'Button',
      label: t('item.photos.actions.add_photo'),
      onClick: () => imageRef.current?.click(),
      disabled: !open || orderStatus != 'Init' || isFetching,
    },
    {
      kind: 'Button',
      label: orderStatus === 'Init' ? t('item.photos.actions.reorder') : t('item.photos.actions.finish'),
      onClick: () => (orderStatus === 'Init' ? setOrderStatus('Editing') : setOrderStatus('Completed')),
      disabled: !open || isFetching,
    },
  ];

  const move = (index: number) => {
    const photo1 = photos[index - 1];
    const photo2 = photos[index];
    if (index != 1 && photo1 && photo2) {
      setPhotos([...photos.slice(0, index - 1), photo2, photo1, ...photos.slice(index + 1)]);
    }
  };

  return (
    <>
      <Container
        title={t('item.photos.title')}
        actions={actions}
        handler={{ open, setOpen }}
        testId="item_photos"
      >
        <>
          {data.kind === 'Called' && data.fetching === true && (
            <div className={styles.loadingContainer}>
              <Skeleton
                height={20}
                variant="rounded"
              />
            </div>
          )}
          {data.kind === 'Error' && <ServiceErrorView error={data.error} />}
          {data.kind === 'Loaded' && (
            <Photos
              setModal={setModal}
              itemId={item.productId}
              photos={photos}
              orderStatus={orderStatus}
              moveLeft={move}
              moveRight={(index: number) => move(index + 1)}
              updateStatus={setOrderStatus}
            />
          )}
          <HiddenFile
            reference={imageRef}
            accept="image/jpeg"
            onChange={setImage}
          />
        </>
      </Container>
      <ModalView modal={modal} />
    </>
  );
};

export default View;
